/* RESPONSIBLE TEAM: team-proactive-support */
import Coordinates from './coordinates';
import { tracked } from '@glimmer/tracking';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';

interface Inputs {
  top: number;
  left: number;
  height: number;
  width: number;
}

export default class Bounds {
  @tracked top: number;
  @tracked left: number;
  @tracked width: number;
  @tracked height: number;

  @computed('left', 'width')
  get right(): number {
    return this.left + this.width;
  }

  @computed('top', 'height')
  get bottom(): number {
    return this.top + this.height;
  }

  get centerPoint(): Coordinates {
    return new Coordinates(this.left + this.width / 2, this.top + this.height / 2);
  }

  constructor(top: number, left: number, width: number, height: number) {
    this.top = top;
    this.left = left;
    this.width = width;
    this.height = height;
  }

  static fromJSON(inputs: Inputs): Bounds {
    let { top, left, width, height } = inputs;
    return new Bounds(top, left, width, height);
  }

  static fromList(list: Array<Bounds>): Bounds {
    let top = Math.min(...list.map((bounds) => bounds.top));
    let left = Math.min(...list.map((bounds) => bounds.left));
    let right = Math.max(...list.map((bounds) => bounds.right));
    let bottom = Math.max(...list.map((bounds) => bounds.bottom));
    return new Bounds(top, left, right - left, bottom - top);
  }

  updateFromJSON(inputs: Inputs) {
    let { top, left, width, height } = inputs;
    if (top !== this.top) {
      this.top = top;
    }
    if (left !== this.left) {
      this.left = left;
    }
    if (width !== this.width) {
      this.width = width;
    }
    if (height !== this.height) {
      this.height = height;
    }
  }

  coversCoordinates(coordinates: Coordinates): boolean {
    return (
      coordinates.x >= this.left &&
      coordinates.x <= this.right &&
      coordinates.y >= this.top &&
      coordinates.y <= this.bottom
    );
  }

  containedWithin(bounds: Bounds): boolean {
    return (
      this.left >= bounds.left &&
      this.right <= bounds.right &&
      this.top >= bounds.top &&
      this.bottom <= bounds.bottom
    );
  }

  overlaps(bounds: Bounds): boolean {
    if (
      bounds.left > this.right ||
      bounds.right < this.left ||
      bounds.top > this.bottom ||
      bounds.bottom < this.top
    ) {
      return false;
    } else {
      return true;
    }
  }

  alignedHorizontallyWith(bounds: Bounds): boolean {
    return (
      Math.round(this.top) === Math.round(bounds.top) &&
      Math.round(this.bottom) === Math.round(bounds.bottom)
    );
  }

  alignedVerticallyWith(bounds: Bounds): boolean {
    return (
      Math.round(this.left) === Math.round(bounds.left) &&
      Math.round(this.right) === Math.round(bounds.right)
    );
  }
}
